import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Edit, ImagePlus, Layers, Trash2, X } from "lucide-react";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";
import StatCard from "../../component/StatCard";
import ActionButtons from "../../component/AdminActionButtons";
import { http } from "../../axios/axios";
import { FormInput, FormSelect, FormTextArea } from "../../helpers/AdminHelper";
import { GetImageUrl } from "../../helpers/GetImageUrl";

function CreateIpNetworkSubCategory({
  closeModal,
  handleSubmit,
  preview,
  setPreview,
  formData,
  setFormData,
  categories,
}) {
  const inputClass =
    "w-full border border-white/10 bg-white/5 text-white px-4 py-3 rounded-xl mt-2 outline-none focus:border-blue-500 transition";

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (preview) URL.revokeObjectURL(preview);
    const url = URL.createObjectURL(file);
    setPreview(url);
    handleChange("image", file);
  };

  const removeImage = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    handleChange("image", null);
  };

  return (
    <motion.div className="fixed inset-0 bg-black/90 backdrop-blur-xl z-50">
      <motion.div
        className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[95%] h-[90vh] bg-[#0a0f1a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between px-8 py-5 border-b border-white/10 bg-white/[0.03]">
          <div>
            <h3 className="text-xl font-black text-white uppercase">Create IT Product Subcategory</h3>
            <p className="text-sm text-slate-400 mt-1">Associate the subcategory to an IP Network category</p>
          </div>
          <button type="button" onClick={closeModal} className="h-10 w-10 rounded-xl bg-white/5 hover:bg-red-500 text-slate-300 flex items-center justify-center">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-5 overflow-y-auto flex-1">
          <div>
            <label className="text-sm text-slate-300">Subcategory Image</label>
            {preview ? (
              <div className="mt-2 relative h-36 rounded-xl border border-white/10 bg-white/5 flex items-center justify-center overflow-hidden">
                <img src={preview} alt="ip subcategory preview" className="h-full w-full object-contain" />
                <button type="button" onClick={removeImage} className="absolute top-2 right-2 h-8 w-8 rounded-full bg-red-500 text-white flex items-center justify-center">
                  <X size={16} />
                </button>
              </div>
            ) : (
              <label className="mt-2 h-36 rounded-xl border border-dashed border-white/20 bg-white/5 flex flex-col items-center justify-center cursor-pointer hover:bg-white/10 transition">
                <ImagePlus size={30} className="text-slate-400" />
                <span className="text-sm text-slate-400 mt-2">Upload image</span>
                <input type="file" hidden accept="image/*" onChange={handleImageChange} />
              </label>
            )}
          </div>

          <FormInput label="Subcategory Name" className={inputClass} placeholder="PoE Switches" value={formData.name} onChange={(e) => handleChange("name", e.target.value)} />
          <FormSelect label="IP Network Category" value={formData.category} onChange={(e) => handleChange("category", e.target.value)} placeholder="Select Category" options={categories.map((category) => ({ value: category._id, label: category.name }))} />
          <FormInput label="Slug" className={inputClass} placeholder="poe-switches" value={formData.slug} onChange={(e) => handleChange("slug", e.target.value)} />
          <FormTextArea label="Description" rows={4} className={inputClass} placeholder="Describe the IP network subcategory" value={formData.description} onChange={(e) => handleChange("description", e.target.value)} />
          <FormInput label="Display Order" type="number" className={inputClass} placeholder="1" value={formData.displayOrder} onChange={(e) => handleChange("displayOrder", e.target.value)} />

          <div className="flex gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" checked={Boolean(formData.featured)} onChange={(e) => handleChange("featured", e.target.checked)} />
              Featured
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" checked={Boolean(formData.isActive)} onChange={(e) => handleChange("isActive", e.target.checked)} />
              Active
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-5 border-t border-white/10">
            <button type="button" onClick={closeModal} className="px-6 py-3 rounded-xl bg-white/5 hover:bg-white/10">Cancel</button>
            <button type="submit" className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white">Save Subcategory</button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

const defaultForm = {
  name: "",
  slug: "",
  category: "",
  description: "",
  image: null,
  displayOrder: "",
  featured: false,
  isActive: true,
};

const IpNetworkSubCategory = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState(defaultForm);
  const [preview, setPreview] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [editId, setEditId] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, limit: 20 });
  const [totalCount, setTotalCount] = useState(0);

  const resetForm = () => {
    setFormData(defaultForm);
    setPreview(null);
    setEditId(null);
  };

  const closeModal = () => {
    resetForm();
    setIsModalOpen(false);
  };

  const fetchCategories = async () => {
    try {
      const res = await http.get("/ip-network/categories", { params: { limit: 1000 } });
      setCategories(res.data.categories || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load categories");
    }
  };

  const fetchSubCategories = async () => {
    try {
      const res = await http.get("/ip-network/subcategories", { params: { page: pagination.page, limit: pagination.limit } });
      setSubCategories(res.data.subCategories || []);
      setTotalCount(res.data.total || 0);
      setPagination((prev) => ({ ...prev, page: res.data.page || 1, totalPages: res.data.totalPages || 1 }));
    } catch (error) {
      console.error("IP NETWORK SUBCATEGORIES ERROR:", error);
      toast.error(error.response?.data?.message || "Failed to load IP Network subcategories");
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchSubCategories();
  }, [pagination.page]);

  const isValidSlug = (slug) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = formData.name?.trim();
    const slug = formData.slug?.trim();
    const category = formData.category;
    const description = formData.description?.trim();

    if (!name) return toast.error("Subcategory name is required");
    if (!category) return toast.error("IP Network category is required");
    if (!slug) return toast.error("Slug is required");
    if (!isValidSlug(slug)) return toast.error("Invalid slug format");
    if (!description) return toast.error("Description is required");
    if (!editId && !formData.image) return toast.error("Subcategory image is required");

    const payload = new FormData();
    payload.append("name", name);
    payload.append("category", category);
    payload.append("slug", slug);
    payload.append("description", description);
    payload.append("displayOrder", formData.displayOrder ?? "");
    payload.append("featured", String(Boolean(formData.featured)));
    payload.append("isActive", String(Boolean(formData.isActive)));
    if (formData.image) payload.append("image", formData.image);

    try {
      if (editId) {
        await http.put(`/ip-network/subcategories/${editId}`, payload);
        toast.success("IP Network subcategory updated successfully");
      } else {
        await http.post("/ip-network/subcategories", payload);
        toast.success("IP Network subcategory created successfully");
      }
      closeModal();
      fetchSubCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong while saving the subcategory");
    }
  };

  const handleEdit = (item) => {
    setEditId(item._id);
    setFormData({
      name: item.name || "",
      slug: item.slug || "",
      category: item.category?._id || item.category || "",
      description: item.description || "",
      image: null,
      displayOrder: item.displayOrder ?? "",
      featured: Boolean(item.featured),
      isActive: item.isActive !== false,
    });
    setPreview(item.image ? GetImageUrl(item.image) : null);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this IP Network subcategory?")) return;
    try {
      await http.delete(`/ip-network/subcategories/${id}`);
      toast.success("IP Network subcategory deleted successfully");
      fetchSubCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete IP Network subcategory");
    }
  };

  return (
    <div className="p-4 md:p-8 min-h-screen text-slate-200">
      <AdminHeader title="IP Network Subcategories" buttonText="Add Subcategory" onClick={() => setIsModalOpen(true)} />

      <div className="mb-8 gap-4">
        <StatCard title="Total IP Subcategories" count={totalCount} icon={Layers} />
      </div>

      {subCategories.length === 0 ? (
        <div className="p-8 rounded-2xl bg-white/5 border border-white/10 text-center text-slate-400">No IP network subcategories found</div>
      ) : (
        <div className="space-y-4">
          {subCategories.map((item) => (
            <div key={item._id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-2 rounded bg-slate-900/40 border border-slate-800 hover:border-slate-700 hover:bg-slate-900/60 transition">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="h-12 w-12 sm:h-16 sm:w-16 rounded bg-white p-2 flex items-center justify-center shrink-0">
                  <img src={GetImageUrl(item.image)} alt={item.name} className="max-h-full max-w-full object-contain" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm lg:text-base">{item.name}</h3>
                  <p className="text-xs text-slate-400">{item.category?.name || "Unassigned"}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className="bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-3 py-2">{item.isActive === false ? "Inactive" : "Active"}</span>
                <ActionButtons onEdit={() => handleEdit(item)} onDelete={() => handleDelete(item._id)} />
              </div>
            </div>
          ))}
        </div>
      )}

      {subCategories.length > 0 && (
        <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 mt-8 px-2">
          <button disabled={pagination.page === 1} onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))} className="w-full sm:w-auto px-4 sm:px-5 py-2 rounded-xl bg-white/10 hover:bg-white/20 disabled:opacity-40 transition text-sm sm:text-base">Previous</button>
          <span className="text-white text-sm sm:text-base whitespace-nowrap">Page {pagination.page} of {pagination.totalPages}</span>
          <button disabled={!pagination.totalPages || pagination.page === pagination.totalPages} onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))} className="w-full sm:w-auto px-4 sm:px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-40 transition text-sm sm:text-base">Next</button>
        </div>
      )}

      <AnimatePresence>
        {isModalOpen && (
          <CreateIpNetworkSubCategory
            closeModal={closeModal}
            handleSubmit={handleSubmit}
            preview={preview}
            setPreview={setPreview}
            formData={formData}
            setFormData={setFormData}
            categories={categories}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default IpNetworkSubCategory;
